// IMPORTANT: Maintain feature parity with `app/presenters/paywall_price_labels_presenter`.
import { t } from "@/i18n-js/instance";
import type { PaywallCurrency } from "@/react/types/Paywall";
import type {
  PaywallPriceFrequency,
  PaywallPriceType,
} from "@/react/types/PaywallPrice";

import { formatPaywallCurrencyAmount } from "./currencies";

const intervalSuffixRoot = "paywall_checkout.price_value.interval_suffix";

interface PriceInput {
  amount: number;
  installments_count?: number;
  interval_type: PaywallPriceFrequency;
  price_type: PaywallPriceType;
  upfront_payment_amount: number;
}

type CurrencyInput = Pick<
  PaywallCurrency,
  "code" | "direction" | "smallest_unit" | "symbol"
>;

const formatAmount = (currency: CurrencyInput, amountInCents: number): string =>
  formatPaywallCurrencyAmount(currency, amountInCents, {
    amountInCents: true,
    includeCurrencyCode: false,
  });

// Ex: "month", "year", "quarter"
// Mirrors PaywallPriceLabelsPresenter::Subscription#interval_qualifier (as_adverb).
export const getPriceIntervalLabel = (
  intervalType: PaywallPriceFrequency,
): string => t([intervalSuffixRoot, intervalType]);

// Ex: "$50", null if no upfront
// Mirrors PaywallPriceLabelsPresenter::Base#upfront_payment_amount_formatted.
export const getPriceUpfrontAmountLabel = (
  currency: CurrencyInput,
  price: Pick<PriceInput, "upfront_payment_amount">,
): string | null => {
  if (!price.upfront_payment_amount) return null;
  return formatAmount(currency, price.upfront_payment_amount);
};

// Ex: "$100" (per installment), null if not an installments price
// Mirrors PaywallPriceLabelsPresenter::Installments#installment_amount_formatted.
export const getPriceInstallmentAmountLabel = (
  currency: CurrencyInput,
  price: Pick<
    PriceInput,
    "amount" | "upfront_payment_amount" | "installments_count"
  >,
): string | null => {
  if (!price.installments_count) return null;
  const installmentAmount = Math.round(
    (price.amount - price.upfront_payment_amount) / price.installments_count,
  );
  return formatAmount(currency, installmentAmount);
};
